// The item icons of the build menus (R1855; ui-map-interface.md §8
// "Specification rows"): one line icon for each building of the catalog and
// one for each tech node and trunk step. The build row draws it in its icon
// column, and the build card and the tech card draw it at 20 px in their
// head. A building is keyed by its kind (`Building`), a tech step by its
// code (`W3`, `F1`), never by a display name, so a rename moves no icon.
//
// The icons are line icons in a 24-unit box, drawn with the stroke of the
// dock icons (R1740): `currentColor`, 1.7 wide, round caps and joins, no
// fill but a small accent. They take the text ink of their place, the hero
// ink of a catalogue item (R1798), and never a faction colour.
//
// Where the game already has a mark for the thing, the icon reuses it, so
// the menu and the map speak one language: the commodity marks of
// `ui/resicon.ts` stand on the three producers, the line icons of the
// mockup kit of rounds 083 to 088 (cargo, flask, anchor, sonar, target,
// shield) stand on Storage, the Research Lab, the Dock, the Sonar
// Station, W1 and R1, the round of the build row stands on W2 and R6,
// and the chart pictograms of the structure symbols (R1103,
// `render/structures/pictogram.ts`) give the motif of the Shipyard (a
// cradle with a hull), the Signal Station (a mast with two radio arcs),
// the Torpedo Battery (a tube on a mount) and Fleet Command (a hall with
// columns). Each branch of the tech tree draws from one family of things:
// the trunk is a wall that grows one course a tier and takes a keystone at
// F4, Warfare draws weapons, Resilience draws shields and hulls, Acoustics
// draws sound, and Industry draws tools and works.

import { Building } from '../gen/proto/aquaphobia';
import { rowIcon } from './overlay/buildrow';

/** The kinds of the catalog: every `Building` but the two wire fillers. */
type BuildingKind = Exclude<Building, Building.BUILDING_UNSPECIFIED | Building.UNRECOGNIZED>;

/** The paths of each building, by kind. The record names every kind, so
 *  `tsc` refuses a new kind with no icon. */
const BUILDING: Readonly<Record<BuildingKind, string>> = {
  // A station with a flag: the Command Centre, the hold's own mark.
  [Building.BUILDING_BASE]: '<path d="M3.5 20h17M6 20v-6h12v6"/><path d="M12 14V4"/>'
    + '<path d="M12 4h5.5L16 6.5 17.5 9H12"/>',
  // The alloy plate over a headframe.
  [Building.BUILDING_ALLOY_MINE]: '<path d="M12 3l5 2.9v5.8L12 14.6l-5-2.9V5.9z"/>'
    + '<path d="M3.5 20.5h17M8 20.5l2-4M16 20.5l-2-4"/>',
  // The biomass star over two furrows.
  [Building.BUILDING_BIOMASS_FARM]: '<path d="M12 3c.5 3 2.5 5 5.5 5.5-3 .5-5 2.5-5.5 5.5'
    + '-.5-3-2.5-5-5.5-5.5 3-.5 5-2.5 5.5-5.5z"/><path d="M3.5 20.5h17M6.5 17.5h11"/>',
  // The volatiles diamond over a derrick.
  [Building.BUILDING_VOLATILES_EXTRACTOR]: '<path d="M12 2.5l4.5 4.5-4.5 4.5L7.5 7z"/>'
    + '<path d="M3.5 20.5h17M8.5 20.5L12 13.5l3.5 7M9.8 18h4.4"/>',
  // A crate: the kit's cargo icon.
  [Building.BUILDING_STORAGE]: '<rect x="4" y="7" width="16" height="12" rx="1.5"/><path d="M4 11h16M10 7v4"/>',
  // A crane with its hook: the Works Yard.
  [Building.BUILDING_CONSTRUCTION]: '<path d="M3.5 20.5h8M7 20.5V4.5h12M7 8l3.5-3.5M18 4.5v4"/>'
    + '<path d="M18 8.5v2.2a1.6 1.6 0 1 1-1.6 1.6"/>',
  // A flask: the kit's lab icon.
  [Building.BUILDING_RESEARCH_LAB]: '<path d="M9.5 3.5h5M10.5 3.5v6l-5.5 9.5a1.2 1.2 0 0 0 1 1.8h12'
    + 'a1.2 1.2 0 0 0 1-1.8l-5.5-9.5v-6"/><path d="M7.5 15h9"/>',
  // A cradle with a hull: the Drydock Shoal pictogram, the Shipyard.
  [Building.BUILDING_WHARF]: '<path d="M4 10.5h16l-2.5 4.5h-11z"/>'
    + '<path d="M7.5 15v5.5M16.5 15v5.5M3.5 20.5h17M10 10.5V8h3.5l1 2.5"/>',
  // An anchor: the kit's anchor icon, the Dock.
  [Building.BUILDING_HARBOUR]: '<circle cx="12" cy="5.5" r="2"/>'
    + '<path d="M12 7.5V20M8 10.5h8M5 14c.3 3.6 3.2 6 7 6s6.7-2.4 7-6"/>',
  // A hall with columns: the Marshal Court pictogram, Fleet Command.
  [Building.BUILDING_LOGISTICS]: '<path d="M3.5 9L12 4l8.5 5z"/>'
    + '<path d="M6.5 11.5v6M10.5 11.5v6M13.5 11.5v6M17.5 11.5v6M4 20.5h16"/>',
  // A lattice mast with two radio arcs: the Relay Spire pictogram.
  [Building.BUILDING_COMMUNICATIONS]: '<path d="M9 20.5l3-11 3 11M10.2 16h3.6"/><circle cx="12" cy="7" r="1.3"/>'
    + '<path d="M8.8 3.8a4.5 4.5 0 0 0 0 6.4M15.2 3.8a4.5 4.5 0 0 1 0 6.4"/>',
  // The kit's sonar icon: the Sonar Station.
  [Building.BUILDING_SONAR]: '<circle cx="12" cy="12" r="1.6"/><path d="M8 8a5.6 5.6 0 0 0 0 8'
    + 'M16 8a5.6 5.6 0 0 1 0 8M5 5a9.9 9.9 0 0 0 0 14M19 5a9.9 9.9 0 0 1 0 14"/>',
  // A hull on a drawing sheet: Ship Design.
  [Building.BUILDING_SHIP_DESIGN]: '<rect x="3.5" y="4.5" width="17" height="15" rx="1.5"/>'
    + '<path d="M6.5 13h11l-2 3h-7z"/><path d="M10 13v-3h3.5l1 3"/>',
  // An arrow down under the surface: Dive Control.
  [Building.BUILDING_RADIATION_CONTROL]: '<path d="M3 6.5c1.5-1.2 3-1.2 4.5 0s3 1.2 4.5 0 3-1.2 4.5 0 3 1.2 4.5 0"/>'
    + '<path d="M12 10v10M8 16l4 4 4-4"/>',
  // A tube on a mount: the Deep Battery pictogram, the Torpedo Battery.
  [Building.BUILDING_TORPEDOBANKS]: '<path d="M5 7.5h10.5a2.5 2.5 0 0 1 0 5H5z"/>'
    + '<path d="M5 6v8M8 12.5L6.5 20M14 12.5l1.5 7.5M4 20h15"/>',
  // A person and a plus: the Recruiting Office.
  [Building.BUILDING_RECRUITMENT]: '<circle cx="9" cy="8" r="3"/>'
    + '<path d="M3.5 19.5c0-3.3 2.5-5.5 5.5-5.5s5.5 2.2 5.5 5.5"/><path d="M18 8v6M15 11h6"/>',
  // A mortarboard: the Academy.
  [Building.BUILDING_ACADEMY]: '<path d="M2.5 9L12 4.5 21.5 9 12 13.5z"/>'
    + '<path d="M6.5 11v4.5c0 1.6 2.5 3 5.5 3s5.5-1.4 5.5-3V11M21.5 9v5"/>',
  // A cross: the Sickbay.
  [Building.BUILDING_HOSPITAL]: '<path d="M9.5 4h5v5.5H20v5h-5.5V20h-5v-5.5H4v-5h5.5z"/>',
  // A cell door with its bars: the Brig.
  [Building.BUILDING_PRISON]: '<rect x="5" y="4" width="14" height="16" rx="1"/><path d="M9.5 4v16M14.5 4v16M5 12h14"/>',
  // A horned sea mine over a rack: the Depot (v2-the-deep.md §11.5). A
  // first icon; the client lane of phase 6 may draw its own.
  [Building.BUILDING_DEPOT]: '<circle cx="12" cy="9" r="3.5"/>'
    + '<path d="M12 3.5v2M6.5 9h2M15.5 9h2M8.2 5.2l1.3 1.3M15.8 5.2l-1.3 1.3M4 16h16M4 20h16M7 16v4M17 16v4"/>',
};

/** The courses of the trunk wall, one more each tier. */
const WALL1 = 'M3.5 20.5h17M5 20.5V16h14v4.5M12 16v4.5';
const WALL2 = `${WALL1}M7 16v-4.5h10V16`;
const WALL3 = `${WALL2}M9.5 11.5V7h5v4.5`;

/** A shield, the motif of Resilience. */
const SHIELD = '<path d="M12 3l7.5 3v5.5c0 4.5-3.2 8-7.5 9.5-4.3-1.5-7.5-5-7.5-9.5V6z"/>';

/** A hull from the side, under a mark of its own. */
const HULL = '<path d="M3 15h18l-2.5 4.5h-13z"/>';

/** A torpedo round from the side, the row icon of a round, under a mark
 *  of its own. */
const ROUND = '<path d="M3 10.5h9.5c2 0 3.5.7 3.5 1.5s-1.5 1.5-3.5 1.5H3z"/><path d="M3 8.5v7"/>';

/** The paths of each tech step, by code. */
const TECH: Readonly<Record<string, string>> = {
  // The trunk: a wall that grows one course a tier, with a keystone at F4.
  F1: `<path d="${WALL1}"/>`,
  F2: `<path d="${WALL2}"/>`,
  F3: `<path d="${WALL3}"/>`,
  F4: `<path d="${WALL3}"/><path d="M12 1.8l2.2 2.2-2.2 2.2L9.8 4z"/>`,

  // Warfare: weapons.
  // The kit's target: Weapon I modules.
  W1: '<circle cx="12" cy="12" r="3"/><path d="M12 3v4M12 17v4M3 12h4M17 12h4"/>',
  // A mount that fires every way: Point Defence (R2777).
  W2: '<circle cx="12" cy="18" r="2"/><path d="M12 14V9M8.5 15l-3-3M15.5 15l3-3M8 18H3.5M16 18h4.5"/>',
  // A reticle under an uplink: Fire Control Uplink.
  W3: '<circle cx="10" cy="14" r="5.5"/><path d="M10 10.5v7M6.5 14h7"/>'
    + '<path d="M15.5 3.5a5 5 0 0 1 5 5M15.5 6.5a2 2 0 0 1 2 2"/>',
  // A burst: Warhead Chemistry (R2777).
  W4: '<path d="M12 3.5L13.5 8.3 18 6 15.7 10.5 20.5 12 15.7 13.5 18 18 13.5 15.7 12 20.5 10.5 15.7'
    + ' 6 18 8.3 13.5 3.5 12 8.3 10.5 6 6 10.5 8.3z"/>',
  // A shell on its base: Heavy Ordnance.
  W5: '<path d="M9 19V10c0-3 1.3-5.5 3-6.5 1.7 1 3 3.5 3 6.5v9z"/><path d="M9 15.5h6M7 20.5h10"/>',
  // A sealed letter: Letters of Marque (R2777).
  W6: '<rect x="3.5" y="5.5" width="17" height="13" rx="1.5"/><path d="M3.5 7l8.5 6 8.5-6"/>'
    + '<circle cx="12" cy="16" r="1.4" fill="currentColor" stroke="none"/>',
  // A capital hull with a turret: Capital Doctrine.
  W7: `${HULL}<path d="M8 15v-3h5v3M13 13l7-3.5"/>`,
  // A swallow-tailed pennant on its staff: Broad Pennant, the keystone (R2777).
  W8: '<path d="M5 21V3"/><path d="M5 4h14.5l-4 4.5 4 4.5H5"/>',

  // Resilience: shields and hulls.
  // The kit's shield: Shields I modules.
  R1: SHIELD,
  // A wrench: Field Repairs.
  R2: '<path d="M14.5 4.5a4.5 4.5 0 0 0-5.3 5.9L4 15.6 8.4 20l5.2-5.2a4.5 4.5 0 0 0 5.9-5.3'
    + 'l-2.8 2.8-2.9-.6-.6-2.9z"/>',
  // A shield with a wave: Shield Harmonics.
  R3: `${SHIELD}<path d="M8 12c1.3-1.5 2.7-1.5 4 0s2.7 1.5 4 0"/>`,
  // A hull under a plus: Maintenance Corps, the Tender.
  R4: `${HULL}<path d="M12 5v7M8.5 8.5h7"/>`,
  // A double hull in section: Hull Reinforcement.
  R5: '<path d="M3 8.5h18M4.5 8.5c0 6.5 3.4 11 7.5 11s7.5-4.5 7.5-11M8 8.5c0 4.3 1.8 7.5 4 7.5s4-3.2 4-7.5"/>',
  // A round struck out: Sentinel Refit.
  R6: `${ROUND}<path d="M6.5 6l8 12M14.5 6l-8 12"/>`,
  // A shield with a lattice: Aegis Lattice.
  R7: `${SHIELD}<path d="M12 5v14.5M5.5 10h13M6.5 15h11"/>`,
  // A pulse in a ring: Damage Control Doctrine.
  R8: '<circle cx="12" cy="12" r="8.5"/><path d="M5 12.5h3l2-4 3 7 2-3h4"/>',

  // Acoustics: sound.
  // A hydrophone on its cable: Hydrophone Arrays.
  A1: '<rect x="9" y="3.5" width="6" height="10" rx="3"/><path d="M6.5 11a5.5 5.5 0 0 0 11 0M12 16.5v4M9 20.5h6"/>',
  // A wave on a tiled slab: Anechoic Coatings.
  A2: '<path d="M3 12c1.2-2.5 2.4-2.5 3.6 0s2.4 2.5 3.6 0"/><rect x="14" y="4" width="6" height="16" rx="1"/>'
    + '<path d="M14 9.5h6M14 14.5h6"/>',
  // A scope with its sweep: Deep Scan Protocols.
  A3: '<circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="4"/><path d="M12 12l6-6"/>',
  // A mask: Signature Masking.
  A4: '<path d="M3 9c3-1.5 6-1.5 9 0 3-1.5 6-1.5 9 0 0 4-2 6.5-4.5 6.5-2 0-3.5-1.3-4.5-3'
    + '-1 1.7-2.5 3-4.5 3C5 15.5 3 13 3 9z"/><path d="M6.5 11h3M14.5 11h3"/>',
  // A hull that tows a line of hydrophones: Towed Arrays.
  A5: '<path d="M3 7.5h7.5L9 10.5H4.5z"/><path d="M9.5 9c4 0 3.5 7 7 7"/>'
    + '<circle cx="17.5" cy="16" r="1.2"/><circle cx="20.5" cy="16" r="1"/>',
  // A noisemaker: Decoys.
  A6: '<path d="M4 9.5h3l4-3.5v12l-4-3.5H4z"/><path d="M15 9a4 4 0 0 1 0 6M17.5 6.5a7.5 7.5 0 0 1 0 11"/>',
  // The sonar arcs struck through: Silent Running.
  A7: '<circle cx="12" cy="12" r="1.6"/><path d="M8 8a5.6 5.6 0 0 0 0 8M16 8a5.6 5.6 0 0 1 0 8M4.5 19.5l15-15"/>',
  // A fleet chevron that dives under the surface: Deep Run.
  A8: '<path d="M3 4.5c1.5-1 3-1 4.5 0s3 1 4.5 0 3-1 4.5 0 3 1 4.5 0"/><path d="M12 21l-5.5-13 5.5 2.8 5.5-2.8z"/>',

  // Industry: tools and works.
  // A speed dial: Drive Tuning (R2777).
  I1: '<path d="M4.5 17.5a8 8 0 1 1 15 0"/><path d="M12 14l3.5-4.5"/><circle cx="12" cy="14" r="1.2"/>',
  // A pick: Deep Mining (R2777).
  I2: '<path d="M5 19l12.3-12.3"/><path d="M10.5 4Q19.5 4.5 20 13.5"/>',
  // Bubbles: Cavitation Control (R2777).
  I3: '<circle cx="8" cy="15" r="3.5"/><circle cx="15.5" cy="9" r="2.3"/>'
    + '<circle cx="17" cy="17" r="1.5"/><circle cx="9.5" cy="6.5" r="1.3"/>',
  // The Data Core chip with a letter: Cryptolinguistics.
  I4: '<rect x="7" y="7" width="10" height="10" rx="1"/>'
    + '<path d="M10 3.5V7M14 3.5V7M10 17v3.5M14 17v3.5M3.5 10H7M3.5 14H7M17 10h3.5M17 14h3.5"/>'
    + '<path d="M10.2 14.5l1.8-5 1.8 5M10.8 13h2.4"/>',
  // Two arrows that trade: Trade Compacts.
  I5: '<path d="M4 8.5h13M14 5.5l3 3-3 3M20 15.5H7M10 12.5l-3 3 3 3"/>',
  // A claw: Salvage Rigs (R2777).
  I6: '<path d="M12 3v6M12 9l-5 4v3.5M12 9l5 4v3.5M7 16.5l2.2 2.2M17 16.5l-2.2 2.2"/>',
  // A works with a stack: Heavy Industry.
  I7: '<path d="M3.5 20.5V11l5 3v-3l5 3v-3l5 3V4.5h2.5v16z"/>',
  // A seal with its ribbons: Foundry Charter.
  I8: '<circle cx="12" cy="9" r="5.5"/><path d="M9 13.5L7.5 21l4.5-2.5 4.5 2.5-1.5-7.5"/>',
};

/** The paths of a building kind, or undefined for a kind with no icon. */
export function buildingPaths(kind: Building): string | undefined {
  return (BUILDING as Readonly<Record<number, string>>)[kind];
}

/** The paths of a tech code, or undefined for a code with no icon. */
export function techPaths(code: string): string | undefined {
  return TECH[code];
}

/** The SVG of one item icon, of the class `bic` that the build row and
 *  the card head size, hidden from a screen reader: the name beside it
 *  names the item. `bi-<key>` names the icon for a test. */
function svg(key: string, paths: string): string {
  return `<svg class="bic bi-${key}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"`
    + ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">'
    + `${paths}</svg>`;
}

/** The icon of a building kind. A kind this client does not know yet (a
 *  newer server) draws the generic building icon. */
export function buildingIcon(kind: Building): string {
  const p = buildingPaths(kind);
  return p === undefined ? rowIcon('building') : svg(`b${kind}`, p);
}

/** The icon of a tech step by its code. A code this client does not know
 *  draws the generic tech icon. */
export function techIcon(code: string): string {
  const p = techPaths(code);
  return p === undefined ? rowIcon('tech') : svg(`t-${code.toLowerCase()}`, p);
}
